import type { Rung } from '@quoting/ladder'
import { useMemo } from 'react'
import { useAuth } from '../lib/auth'
import { amount, signedAmount } from '../lib/format'
import { cx, Panel } from './ui'
import { localName, useT } from '../lib/i18n'

type T = (s: string) => string
function rungLabel(r: Rung, t: T, lang: 'en' | 'de'): string {
  if (r.kind === 'cost') return r.name === 'Cost' || !r.name ? t('Cost') : r.name
  if (r.kind === 'uplift') return t('Commercial uplift')
  if (r.kind === 'differential') return t('Packaging')
  if (r.kind === 'corridor') return t(r.name.replace('Corridor · ', 'Corridor, '))
  if (r.kind === 'intercept') return t('Intercept')
  if (r.kind === 'absBase') return t('Base surcharge')
  return localName(lang, r.name, r.nameDe)
}

interface Column {
  key: string
  label: string
  sub?: string | undefined
  title?: string | undefined
  level?: string | undefined
  /** names folded into this column (zero-effect drivers of one block) */
  names?: string[]
  group: string
  /** bar from `from` to `to` in price space; `total` bars start at the axis floor */
  from: number
  to: number
  total?: boolean
  kind: 'cost' | 'step' | 'target' | 'zero'
  tone: 'cost' | 'up' | 'down' | 'uplift' | 'corridor' | 'target'
}

const BAR: Record<Column['tone'], string> = {
  cost: 'bg-slate-700',
  up: 'bg-sky-500',
  down: 'bg-rose-500',
  uplift: 'bg-gold',
  corridor: 'bg-amber-500',
  target: 'bg-sky-500',
}

/**
 * The ladder as a left-to-right waterfall: cost on the left, one column per rung climbing to
 * the target on the right. Every driver is shown, zeros included, labelled with its level.
 */
export function Ladder({
  ladder,
  target,
  currency,
  compact = false,
  groups: showGroups = true,
  subtitle,
  packaging,
}: {
  ladder: Rung[]
  target: number
  currency: string
  compact?: boolean
  /** block brackets under the columns; off when the rungs already are the blocks */
  groups?: boolean
  subtitle?: string | undefined
  /** the packaging variant priced, shown as the differential rung's level */
  packaging?: string | null | undefined
}) {
  const { model } = useAuth()
  const { t, lang } = useT()
  const FALLBACK: Record<string, string> = { market: 'Market situation', structure: 'Structural position', client: 'Client context', deal: 'Deal context' }
  const blockName = (key: string | undefined) => {
    const b = model?.blocks.find((x) => x.key === key)
    return b ? localName(lang, b.name, b.nameDe) : key ? t(FALLBACK[key] ?? key) : ''
  }
  const levelLabel = (id: string, level: string | undefined): string | undefined => {
    if (level === undefined) return undefined
    const d = model?.drivers.find((x) => x.id === id)
    return (lang === 'de' ? d?.levelLabelsDe?.[d.levels.indexOf(level)] : undefined) ?? d?.levelLabels?.[d.levels.indexOf(level)] ?? level
  }
  const anchorsFor = (id: string): string | undefined => {
    const d = model?.drivers.find((x) => x.id === id)
    if (!d || (!d.anchorLow && !d.anchorHigh)) return undefined
    return `${d.levels[0]}: ${d.anchorLow ?? ''}\n${d.levels[d.levels.length - 1]}: ${d.anchorHigh ?? ''}${d.reversed ? '\n(scale reads against its natural magnitude)' : ''}`
  }

  const columns = useMemo<Column[]>(() => {
    const kept = ladder.filter((r) => !((r.kind === 'intercept' || r.kind === 'absBase' || r.kind === 'corridor') && r.amount === 0))
    // the packaging differential is a deal term: show it with the deal block, not after the uplift
    const diff = kept.find((r) => r.kind === 'differential')
    const rungs = diff ? kept.filter((r) => r !== diff) : kept
    if (diff) {
      let at = rungs.findIndex((r) => r.kind === 'uplift')
      if (at < 0) at = rungs.length
      rungs.splice(at, 0, diff)
    }
    const out: Column[] = []
    let running = 0
    let seenUplift = false
    for (const r of rungs) {
      if (r.kind === 'cost') {
        out.push({ key: r.id, label: rungLabel(r, t, lang), group: t('Cost'), from: 0, to: r.amount, total: true, kind: 'cost', tone: 'cost' })
        running = r.amount
        continue
      }
      let group: string
      if (r.kind === 'driver') group = r.driverKind === 'absolute' && seenUplift ? `${blockName(r.block)} · ${t('€ surcharges')}` : blockName(r.block)
      else if (r.kind === 'uplift') {
        group = t('Uplift')
        seenUplift = true
      } else if (r.kind === 'intercept') group = t('Cost')
      else if (r.kind === 'absBase') group = t('Base surcharge')
      else if (r.kind === 'differential') group = blockName('deal')
      else group = t('Corridor')
      const zero = Math.abs(r.amount) < 1e-9
      const prev = out[out.length - 1]
      if (zero && r.kind === 'driver' && prev && prev.kind === 'zero' && prev.group === group && prev.names) {
        prev.names.push(rungLabel(r, t, lang))
        prev.label = t('{n} drivers', { n: prev.names.length })
        prev.title = prev.names.join('\n')
        prev.level = undefined
        continue
      }
      out.push({
        key: r.id,
        label: rungLabel(r, t, lang),
        sub: r.nameDe,
        title: r.kind === 'driver' ? [r.nameDe, anchorsFor(r.id)].filter(Boolean).join('\n') : undefined,
        level: r.kind === 'differential' ? packaging ?? undefined : levelLabel(r.id, r.level),
        ...(zero && r.kind === 'driver' ? { names: [rungLabel(r, t, lang)] } : {}),
        group,
        from: running,
        to: running + r.amount,
        kind: zero ? 'zero' : 'step',
        tone: r.amount < 0 ? 'down' : r.kind === 'uplift' ? 'uplift' : r.kind === 'corridor' ? 'corridor' : 'up',
      })
      running += r.amount
    }
    out.push({ key: '__target', label: t('Target'), group: t('Target'), from: 0, to: target, total: true, kind: 'target', tone: 'target' })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ladder, target, model, lang])

  const values = columns.flatMap((c) => [c.from, c.to]).filter((v) => v > 0)
  const lowest = Math.min(...values)
  const highest = Math.max(...values)
  const span = Math.max(highest - lowest, 1)
  const floorV = lowest - span * 0.35 // the axis starts below the cost so the cost bar has some body
  const ceilV = highest + span * 0.12
  const pct = (v: number) => ((v - floorV) / (ceilV - floorV)) * 100

  // block brackets: consecutive columns sharing a group
  const groups: { title: string; span: number }[] = []
  for (const c of columns) {
    const last = groups[groups.length - 1]
    if (last && last.title === c.group) last.span++
    else groups.push({ title: c.group, span: 1 })
  }

  const H = compact ? 220 : 280
  return (
    <Panel title={t('The ladder')} subtitle={subtitle}>
      <div className="-mx-6 overflow-x-auto px-6">
        <div>
          {/* chart */}
          <div className="relative" style={{ height: H }}>
            {/* gridlines */}
            {[0.25, 0.5, 0.75, 1].map((f) => (
              <div key={f} className="absolute left-0 right-0 border-t border-dashed border-slate-800" style={{ bottom: `${f * 100}%` }} />
            ))}
            <div className="absolute inset-0 flex items-end">
              {columns.map((c, i) => {
                const lo = Math.min(c.from, c.to)
                const hi = Math.max(c.from, c.to)
                const bottom = c.total ? 0 : pct(lo)
                const height = c.total ? pct(c.to) : Math.max(pct(hi) - pct(lo), 0)
                const next = columns[i + 1]
                return (
                  <div key={c.key} className="relative h-full min-w-0 flex-1">
                    {/* connector to the next column at this column's running level */}
                    {next && !next.total && (
                      <div className="absolute left-1/2 right-0 border-t border-dashed border-slate-600" style={{ bottom: `${pct(c.to)}%` }} />
                    )}
                    {c.kind === 'zero' ? (
                      <div className="absolute left-[22%] right-[22%] h-[3px] rounded bg-slate-500" style={{ bottom: `calc(${pct(c.to)}% - 1px)` }} />
                    ) : (
                      <div
                        className={cx('absolute left-[18%] right-[18%] rounded-[3px]', BAR[c.tone], c.kind === 'target' && 'opacity-90')}
                        style={{ bottom: `${bottom}%`, height: `${Math.max(height, 0.6)}%` }}
                      />
                    )}
                    {/* value label */}
                    <div
                      className={cx(
                        'absolute left-0 right-0 text-center text-[11px] font-semibold tabular',
                        c.kind === 'zero' ? 'text-slate-500' : c.tone === 'down' ? 'text-rose-500' : c.kind === 'cost' ? 'text-slate-400' : 'text-slate-50',
                      )}
                      style={c.tone === 'down' && !c.total ? { top: `calc(${100 - pct(lo)}% + 4px)` } : { bottom: `calc(${c.total ? pct(c.to) : pct(hi)}% + 6px)` }}
                    >
                      {c.total ? amount(c.to) : c.kind === 'zero' ? '0' : signedAmount(c.to - c.from)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* level pills */}
          <div className={cx('mt-3 flex', !columns.some((c) => c.level !== undefined) && 'hidden')}>
            {columns.map((c) => (
              <div key={c.key} className="flex min-w-0 flex-1 justify-center">
                {c.level !== undefined ? (
                  <span
                    title={c.title}
                    className={cx('inline-flex h-6 min-w-[2rem] items-center justify-center whitespace-nowrap rounded-md px-1.5 text-[12px] font-semibold tabular', c.kind === 'zero' ? 'bg-slate-900 text-slate-500' : 'bg-sky-500/10 text-sky-500')}
                  >
                    {c.level}
                  </span>
                ) : (
                  <span className="h-6" />
                )}
              </div>
            ))}
          </div>

          {/* names */}
          <div className="mt-1.5 flex">
            {columns.map((c) => (
              <div key={c.key} className="min-w-0 flex-1 px-1 text-center" title={c.title ?? c.sub}>
                <div className={cx('text-[11px] leading-[1.25]', c.kind === 'zero' ? 'text-slate-500' : 'text-slate-200', c.kind === 'target' && 'font-semibold text-sky-500')} style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere', hyphens: 'auto' }}>
                  {c.label}
                  {c.names && c.names.length > 1 && <span className="block text-[10px] text-slate-600">{t('no effect')}</span>}
                </div>
              </div>
            ))}
          </div>

          {/* block brackets */}
          {showGroups && (
            <div className="mt-3 flex">
              {groups.map((g, i) => (
                <div key={`${g.title}-${i}`} className="min-w-0 px-1" style={{ flex: g.span }}>
                  <div className="border-t border-slate-700 pt-1.5 text-center text-[9px] font-semibold uppercase tracking-[0.06em] text-slate-500">
                    <span className="block whitespace-nowrap">{g.title}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Panel>
  )
}
